/**
 * HTTP implementation of the Config API contract.
 *
 * One `fetch` wrapper, one error type, one place the bearer token is attached.
 * Routes match section 5 of the design doc exactly; `{type}` is always a
 * registry key taken from the schema response, never a string this file makes
 * up, so an unknown type 404s on the server before any SQL is built.
 */

import type {
  AccessChange,
  AccessPerson,
  AccessResponse,
  AuditResponse,
  ConfigRow,
  ListResponse,
  SchemaResponse,
  ApiErrorBody,
} from './types'

/** Thrown for any non-2xx. `fields` carries per-field messages from a 422. */
export class ApiError extends Error {
  readonly status: number
  readonly detail?: string
  readonly fields?: Record<string, string>

  constructor(status: number, body: ApiErrorBody | string) {
    const parsed = typeof body === 'string' ? { error: body } : body
    super(parsed.error || `Request failed (${status})`)
    this.name = 'ApiError'
    this.status = status
    this.detail = parsed.detail
    this.fields = parsed.fields
  }

  /** 401/403 are the two the UI reacts to structurally rather than as a toast. */
  get isAuth(): boolean {
    return this.status === 401 || this.status === 403
  }
}

export interface ListParams {
  search?: string
  page?: number
  page_size?: number
  sort?: string
  dir?: 'asc' | 'desc'
  /** Registry-declared filters, passed through as query parameters. */
  filters?: Record<string, string | undefined>
}

export interface AuditParams {
  /** Only this region's entries (every entity is region-qualified). */
  region?: string
  entity?: string
  entity_key?: string
  page?: number
  page_size?: number
}

/** Everything the SPA may ask the Config API for. */
export interface ConfigApi {
  getSchema(): Promise<SchemaResponse>
  /** First sign-in only. Returns the fresh schema. */
  registerRegion(region: string): Promise<SchemaResponse>
  list(type: string, params?: ListParams): Promise<ListResponse>
  get(type: string, key: string): Promise<ConfigRow>
  history(type: string, key: string): Promise<AuditResponse>
  /** Keys a new row could be created for (see KeyFieldSchema.available_keys). */
  availableKeys(type: string): Promise<{ keys: string[] }>
  put(type: string, key: string, body: ConfigRow): Promise<ConfigRow>
  remove(type: string, key: string): Promise<void>
  audit(params?: AuditParams): Promise<AuditResponse>
  listPermissions(): Promise<AccessResponse>
  putPermission(userOid: string, change: AccessChange): Promise<AccessPerson>
  deletePermission(userOid: string): Promise<void>
}

/** Returns a bearer token. */
export type TokenProvider = () => Promise<string | null>

function buildQuery(params: object | undefined): string {
  if (!params) return ''
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    search.set(key, String(value))
  }
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

export function createHttpApi(baseUrl: string, getToken: TokenProvider): ConfigApi {
  const root = baseUrl.replace(/\/$/, '')

  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = await getToken()
    const headers = new Headers(init.headers)
    headers.set('Accept', 'application/json')
    if (init.body) headers.set('Content-Type', 'application/json')
    if (token) headers.set('Authorization', `Bearer ${token}`)

    const response = await fetch(`${root}${path}`, { ...init, headers })

    if (response.status === 204) return undefined as T

    const text = await response.text()
    const payload = text ? safeJson(text) : null

    if (!response.ok) {
      throw new ApiError(response.status, (payload as ApiErrorBody) ?? text ?? 'Request failed')
    }
    return payload as T
  }

  function safeJson(text: string): unknown {
    try {
      return JSON.parse(text)
    } catch {
      return { error: text.slice(0, 500) }
    }
  }

  /**
   * Registry keys and row keys both land in the path. Row keys are arbitrary
   * user input (a debtor code, a mailbox address), so they are encoded — an
   * address with a `+` or a `#` would otherwise silently address a different row.
   */
  const seg = (value: string) => encodeURIComponent(value)

  return {
    getSchema: () => request<SchemaResponse>('/api/config/schema'),

    registerRegion: (region) =>
      request<SchemaResponse>('/api/me/region', {
        method: 'POST',
        body: JSON.stringify({ region }),
      }),

    list: (type, params = {}) =>
      request<ListResponse>(
        `/api/config/${seg(type)}${buildQuery({
          search: params.search,
          page: params.page,
          page_size: params.page_size,
          sort: params.sort,
          dir: params.dir,
          ...(params.filters ?? {}),
        })}`,
      ),

    get: (type, key) => request<ConfigRow>(`/api/config/${seg(type)}/${seg(key)}`),

    history: (type, key) =>
      request<AuditResponse>(`/api/config/${seg(type)}/${seg(key)}/history`),

    availableKeys: (type) => request<{ keys: string[] }>(`/api/available-keys/${seg(type)}`),

    put: (type, key, body) =>
      request<ConfigRow>(`/api/config/${seg(type)}/${seg(key)}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),

    remove: (type, key) =>
      request<void>(`/api/config/${seg(type)}/${seg(key)}`, { method: 'DELETE' }),

    audit: (params = {}) => request<AuditResponse>(`/api/config/audit${buildQuery(params)}`),

    listPermissions: () => request<AccessResponse>('/api/permissions'),

    putPermission: (userOid, change) =>
      request<AccessPerson>(`/api/permissions/${seg(userOid)}`, {
        method: 'PUT',
        body: JSON.stringify(change),
      }),

    deletePermission: (userOid) =>
      request<void>(`/api/permissions/${seg(userOid)}`, { method: 'DELETE' }),
  }
}
